import {
  type AssumedWorkpaperResource,
  type BasicInformationResource,
  type BusinessActivityResource,
  type ContactAddressResource,
  type DependentResource,
  type Form1099PaymentsInfoResource,
  type GetWorklayerJobExtractionStatusResponse,
  type InventoryInfoResource,
  type PropertyInformationResource,
  PropertyType,
  type RentalActivityResource,
  type SpouseResource,
  type TaxActivityAssigneeResource,
  type TaxPayerResource,
  type TaxPreparationDataResource,
  type WorklayerJobResource,
  type WorkpaperAssigneeResource,
  type WorkpaperAssignmentResource,
} from '@taxfyle/txf-api-client'
import { match } from '@/utils/pattern-match-util'
import type {
  AssumedWorkpaperProps,
  BasicInformationProps,
  BusinessActivityProps,
  ContactAddress,
  DependentProps,
  FilingYear,
  Form1099PaymentsInfo,
  InventoryInfo,
  PropertyInformation,
  RentalActivityProps,
  SpouseProps,
  TaxActivityAssignee,
  TaxPayerProps,
  TaxPreparationDataProps,
  TxfWorklayerJobProps,
  WorkpaperAssignee,
} from './models'
import { TxfWorklayerJobExtractionStatus } from './models/TxfWorklayerJobExtractionStatus'

export function mapWorklayerJobResourceToProps(
  resource: WorklayerJobResource,
): TxfWorklayerJobProps {
  return {
    id: resource.job_id,
    filingYears: resource.filing_years.map(mapFilingYearResourceToProps),
  }
}

function mapFilingYearResourceToProps(
  resource: WorklayerJobResource['filing_years'][number],
): FilingYear {
  return {
    id: resource.id,
    year: resource.year,
    inactive: resource.inactive,
    dateCreated: resource.date_created,
  }
}

export function mapTaxPreparationDataResourceToProps(
  resource: TaxPreparationDataResource,
): TaxPreparationDataProps {
  return {
    basicInformation: mapBasicInformationResourceToProps(
      resource.basic_information,
    ),
    businesses: resource.tax_activities.businesses.map(
      mapBusinessActivityResourceToProps,
    ),
    rentals: resource.tax_activities.rentals.map(
      mapRentalActivityResourceToProps,
    ),
    workpapers: resource.workpapers.map(mapAssumedWorkpaperResourceToProps),
  }
}

function mapBasicInformationResourceToProps(
  resource: BasicInformationResource,
): BasicInformationProps {
  return {
    taxPayer: mapTaxPayerResourceToProps(resource.tax_payer),
    spouse: resource.spouse ? mapSpouseResourceToProps(resource.spouse) : null,
    dependents: resource.dependents.map(mapDependentResourceToProps),
  }
}

function mapTaxPayerResourceToProps(resource: TaxPayerResource): TaxPayerProps {
  return {
    id: resource.id,
    firstName: resource.first_name ?? null,
    middleInitial: resource.middle_initial ?? null,
    lastName: resource.last_name ?? null,
    tin: resource.tin ?? null,
    dob: resource.dob ?? null,
    phoneNumber: resource.phone_number ?? null,
    emailAddress: resource.email_address ?? null,
    occupation: resource.occupation ?? null,
    filingStatus: resource.filing_status ?? null,
    contactAddress: resource.contact_address
      ? mapContactAddressResourceToAddress(resource.contact_address)
      : null,
  }
}

function mapSpouseResourceToProps(resource: SpouseResource): SpouseProps {
  return {
    id: resource.id,
    firstName: resource.first_name ?? null,
    middleInitial: resource.middle_initial ?? null,
    lastName: resource.last_name ?? null,
    tin: resource.tin ?? null,
    dob: resource.dob ?? null,
    phoneNumber: resource.phone_number ?? null,
    emailAddress: resource.email_address ?? null,
    occupation: resource.occupation ?? null,
  }
}

function mapDependentResourceToProps(
  resource: DependentResource,
): DependentProps {
  return {
    id: resource.id,
    firstName: resource.first_name ?? null,
    middleInitial: resource.middle_initial ?? null,
    lastName: resource.last_name ?? null,
    tin: resource.tin ?? null,
    dob: resource.dob ?? null,
    relationship: resource.relationship ?? null,
    archived: resource.archived,
  }
}

function mapContactAddressResourceToAddress(
  resource: ContactAddressResource,
): ContactAddress {
  return {
    streetLine: resource.street_line ?? null,
    city: resource.city ?? null,
    postalCode: resource.postal_code ?? null,
    region: resource.region ?? null,
    country: resource.country ?? null,
  }
}

function mapBusinessActivityResourceToProps(
  resource: BusinessActivityResource,
): BusinessActivityProps {
  return {
    id: resource.id,
    archived: resource.archived,
    businessType: resource.business_type,
    businessName: resource.business_name ?? null,
    ein: resource.ein ?? null,
    principleBusiness: resource.principle_business ?? null,
    businessCode: resource.business_code ?? null,
    assignedTo: mapTaxActivityAssigneeResourceToAssignee(resource.assigned_to),
    didNotMateriallyParticipateDuringYear:
      resource.did_not_materially_participate_during_year ?? null,
    businessWasStartedOrAcquiredDuringYear:
      resource.business_was_started_or_acquired_during_year ?? null,
    businessWasDisposedOfDuringYear:
      resource.business_was_disposed_of_during_year ?? null,
    carryToForm8960: resource.carry_to_form_8960 ?? null,
    professionalGambler: resource.professional_gambler ?? null,
    exemptNotaryIncome: resource.exempt_notary_income ?? null,
    paperRouteExcludedFromSe: resource.paper_route_excluded_from_se ?? null,
    form1099Payments: resource.form_1099_payments
      ? mapForm1099PaymentsResourceToInfo(resource.form_1099_payments)
      : null,
    inventory: resource.has_inventory
      ? mapInventoryResourceToInfo(resource.has_inventory)
      : null,
    address: resource.address
      ? mapContactAddressResourceToAddress(resource.address)
      : null,
  }
}

function mapRentalActivityResourceToProps(
  resource: RentalActivityResource,
): RentalActivityProps {
  return {
    id: resource.id,
    archived: resource.archived,
    assignedTo: mapTaxActivityAssigneeResourceToAssignee(resource.assigned_to),
    propertyDescription: resource.property_description ?? null,
    treatAs: resource.treat_as ?? null,
    percentageOfOwnership: resource.percentage_of_ownership ?? null,
    percentageOfTenantOccupancy:
      resource.percentage_of_tenant_occupancy ?? null,
    fairRentalDays: resource.fair_rental_days ?? null,
    personalUseDays: resource.personal_use_days ?? null,
    propertyInformation: resource.property_information
      ? mapPropertyInformationResourceToInfo(resource.property_information)
      : null,
    propertyPlacedInServiceDuringYear:
      resource.property_placed_in_service_during_year ?? null,
    propertyWasDisposedOfDuringYear:
      resource.property_was_disposed_of_during_year ?? null,
    carryToForm8960: resource.carry_to_form_8960 ?? null,
    mainHomeOrSecondHome: resource.main_home_or_second_home ?? null,
    qualifiedJointVenture: resource.qualified_joint_venture ?? null,
    electingOutOfBusinessInterestExpenseLimit:
      resource.electing_out_of_business_interest_expense_limit ?? null,
    isTradeOrBusiness: resource.is_trade_or_business ?? null,
    form1099Payments: resource.form_1099_payments
      ? mapForm1099PaymentsResourceToInfo(resource.form_1099_payments)
      : null,
    address: resource.address
      ? mapContactAddressResourceToAddress(resource.address)
      : null,
  }
}

function mapAssumedWorkpaperResourceToProps(
  resource: AssumedWorkpaperResource,
): AssumedWorkpaperProps {
  return {
    id: resource.id,
    type: resource.type,
    assignedTo: mapWorkpaperAssigneeResourceToAssignee(resource.assigned_to),
  }
}

function mapTaxActivityAssigneeResourceToAssignee(
  resource: TaxActivityAssigneeResource,
): TaxActivityAssignee {
  return match(resource, 'type', {
    TAX_PAYER: ({ id }) => ({ type: 'TAX_PAYER' as const, id }),
    SPOUSE: ({ id }) => ({ type: 'SPOUSE' as const, id }),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    JOINT: ({ tax_payer_id, spouse_id }) => ({
      type: 'JOINT' as const,
      taxPayerId: tax_payer_id,
      spouseId: spouse_id,
    }),
    UNASSIGNED: () => ({ type: 'UNASSIGNED' as const }),
  }) as TaxActivityAssignee
}

function mapWorkpaperAssigneeResourceToAssignee(
  resource: WorkpaperAssigneeResource,
): WorkpaperAssignee {
  return match(resource, 'type', {
    TAX_PAYER: ({ id }) => ({ type: 'TAX_PAYER' as const, id }),
    SPOUSE: ({ id }) => ({ type: 'SPOUSE' as const, id }),
    DEPENDENT: ({ id }) => ({ type: 'DEPENDENT' as const, id }),
    BUSINESS: ({ id }) => ({ type: 'BUSINESS' as const, id }),
    RENTAL: ({ id }) => ({ type: 'RENTAL' as const, id }),
    // eslint-disable-next-line @typescript-eslint/naming-convention
    JOINT: ({ tax_payer_id, spouse_id }) => ({
      type: 'JOINT' as const,
      taxPayerId: tax_payer_id,
      spouseId: spouse_id,
    }),
    UNASSIGNED: () => ({ type: 'UNASSIGNED' as const }),
  }) as WorkpaperAssignee
}

function mapForm1099PaymentsResourceToInfo(
  resource: Form1099PaymentsInfoResource,
): Form1099PaymentsInfo {
  if (!resource.did_make_payments) {
    return { didMakePayments: false }
  }
  return {
    didMakePayments: true,
    willFileForm1099: resource.will_file_form_1099,
  }
}

function mapInventoryResourceToInfo(
  resource: InventoryInfoResource,
): InventoryInfo {
  if (!resource.has_inventory) {
    return { hasInventory: false }
  }
  return {
    hasInventory: true,
    methodUsedToValueClosingInventory:
      resource.method_used_to_value_closing_inventory ?? null,
  }
}

function mapPropertyInformationResourceToInfo(
  resource: PropertyInformationResource,
): PropertyInformation {
  if (resource.type === PropertyType.Other) {
    return {
      type: resource.type,
      description: resource.description ?? null,
    }
  }
  return { type: resource.type }
}

export function mapTaxPayerPropsToResource(
  props: TaxPayerProps,
): TaxPayerResource {
  return {
    id: props.id ?? '',
    first_name: props.firstName ?? undefined,
    middle_initial: props.middleInitial ?? undefined,
    last_name: props.lastName ?? undefined,
    tin: props.tin ?? undefined,
    dob: props.dob ?? undefined,
    phone_number: props.phoneNumber ?? undefined,
    email_address: props.emailAddress ?? undefined,
    occupation: props.occupation ?? undefined,
    filing_status: props.filingStatus ?? undefined,
    contact_address: props.contactAddress
      ? mapContactAddressToResource(props.contactAddress)
      : undefined,
  }
}

export function mapSpousePropsToResource(props: SpouseProps): SpouseResource {
  return {
    id: props.id ?? '',
    first_name: props.firstName ?? undefined,
    middle_initial: props.middleInitial ?? undefined,
    last_name: props.lastName ?? undefined,
    tin: props.tin ?? undefined,
    dob: props.dob ?? undefined,
    phone_number: props.phoneNumber ?? undefined,
    email_address: props.emailAddress ?? undefined,
    occupation: props.occupation ?? undefined,
  }
}

export function mapDependentPropsToResource(
  props: DependentProps,
): DependentResource {
  return {
    id: props.id ?? '',
    first_name: props.firstName ?? undefined,
    middle_initial: props.middleInitial ?? undefined,
    last_name: props.lastName ?? undefined,
    tin: props.tin ?? undefined,
    dob: props.dob ?? undefined,
    relationship: props.relationship ?? undefined,
    archived: props.archived,
  }
}

export function mapContactAddressToResource(
  address: ContactAddress,
): ContactAddressResource {
  return {
    street_line: address.streetLine ?? undefined,
    city: address.city ?? undefined,
    postal_code: address.postalCode ?? undefined,
    region: address.region ?? undefined,
    country: address.country ?? undefined,
  }
}

export function mapBusinessActivityPropsToResource(
  props: BusinessActivityProps,
): BusinessActivityResource {
  return {
    id: props.id ?? '',
    archived: props.archived,
    assigned_to: mapTaxActivityAssigneeToResource(props.assignedTo),
    business_type: props.businessType,
    business_name: props.businessName ?? undefined,
    ein: props.ein ?? undefined,
    principle_business: props.principleBusiness ?? undefined,
    business_code: props.businessCode ?? undefined,
    did_not_materially_participate_during_year:
      props.didNotMateriallyParticipateDuringYear ?? undefined,
    business_was_started_or_acquired_during_year:
      props.businessWasStartedOrAcquiredDuringYear ?? undefined,
    business_was_disposed_of_during_year:
      props.businessWasDisposedOfDuringYear ?? undefined,
    carry_to_form_8960: props.carryToForm8960 ?? undefined,
    professional_gambler: props.professionalGambler ?? undefined,
    exempt_notary_income: props.exemptNotaryIncome ?? undefined,
    paper_route_excluded_from_se: props.paperRouteExcludedFromSe ?? undefined,
    form_1099_payments: props.form1099Payments
      ? mapForm1099PaymentsInfoToResource(props.form1099Payments)
      : undefined,
    has_inventory: props.inventory
      ? mapInventoryInfoToResource(props.inventory)
      : undefined,
    address: props.address
      ? mapContactAddressToResource(props.address)
      : undefined,
  }
}

export function mapRentalActivityPropsToResource(
  props: RentalActivityProps,
): RentalActivityResource {
  return {
    id: props.id ?? '',
    archived: props.archived,
    assigned_to: mapTaxActivityAssigneeToResource(props.assignedTo),
    property_description: props.propertyDescription ?? undefined,
    treat_as: props.treatAs ?? undefined,
    percentage_of_ownership: props.percentageOfOwnership ?? undefined,
    percentage_of_tenant_occupancy:
      props.percentageOfTenantOccupancy ?? undefined,
    fair_rental_days: props.fairRentalDays ?? undefined,
    personal_use_days: props.personalUseDays ?? undefined,
    property_information: props.propertyInformation
      ? mapPropertyInformationToResource(props.propertyInformation)
      : undefined,
    property_placed_in_service_during_year:
      props.propertyPlacedInServiceDuringYear ?? undefined,
    property_was_disposed_of_during_year:
      props.propertyWasDisposedOfDuringYear ?? undefined,
    carry_to_form_8960: props.carryToForm8960 ?? undefined,
    main_home_or_second_home: props.mainHomeOrSecondHome ?? undefined,
    qualified_joint_venture: props.qualifiedJointVenture ?? undefined,
    electing_out_of_business_interest_expense_limit:
      props.electingOutOfBusinessInterestExpenseLimit ?? undefined,
    is_trade_or_business: props.isTradeOrBusiness ?? undefined,
    form_1099_payments: props.form1099Payments
      ? mapForm1099PaymentsInfoToResource(props.form1099Payments)
      : undefined,
    address: props.address
      ? mapContactAddressToResource(props.address)
      : undefined,
  }
}

function mapTaxActivityAssigneeToResource(
  assignee: TaxActivityAssignee | null,
): TaxActivityAssigneeResource {
  if (!assignee) {
    return { type: 'UNASSIGNED' as const }
  }
  return match(assignee, 'type', {
    TAX_PAYER: ({ id }) => ({ type: 'TAX_PAYER' as const, id }),
    SPOUSE: ({ id }) => ({ type: 'SPOUSE' as const, id }),
    JOINT: ({ taxPayerId, spouseId }) => ({
      type: 'JOINT' as const,
      tax_payer_id: taxPayerId,
      spouse_id: spouseId,
    }),
    UNASSIGNED: () => ({ type: 'UNASSIGNED' as const }),
  }) as TaxActivityAssigneeResource
}

function mapForm1099PaymentsInfoToResource(
  info: Form1099PaymentsInfo,
): Form1099PaymentsInfoResource {
  if (!info.didMakePayments) {
    return { did_make_payments: false }
  }
  return {
    did_make_payments: true,
    will_file_form_1099: info.willFileForm1099,
  }
}

function mapInventoryInfoToResource(
  info: InventoryInfo,
): InventoryInfoResource {
  if (!info.hasInventory) {
    return { has_inventory: false }
  }
  return {
    has_inventory: true,
    method_used_to_value_closing_inventory:
      info.methodUsedToValueClosingInventory ?? undefined,
  }
}

function mapPropertyInformationToResource(
  info: PropertyInformation,
): PropertyInformationResource {
  if (info.type === PropertyType.Other) {
    return {
      type: info.type,
      description: info.description ?? undefined,
    }
  }
  return { type: info.type }
}

export function mapAssumedWorkpaperPropsToAssignmentResource(
  props: AssumedWorkpaperProps,
): WorkpaperAssignmentResource {
  const assignment = mapWorkpaperAssigneeToAssignmentFields(props.assignedTo)
  return {
    workpaper_id: props.id ?? '',
    ...assignment,
  }
}

function mapWorkpaperAssigneeToAssignmentFields(assignee: WorkpaperAssignee): {
  assigned_to?: string
  joint_assigned_to?: string
} {
  return match(assignee, 'type', {
    TAX_PAYER: ({ id }) => ({ assigned_to: id, joint_assigned_to: undefined }),
    SPOUSE: ({ id }) => ({ assigned_to: id, joint_assigned_to: undefined }),
    DEPENDENT: ({ id }) => ({ assigned_to: id, joint_assigned_to: undefined }),
    BUSINESS: ({ id }) => ({ assigned_to: id, joint_assigned_to: undefined }),
    RENTAL: ({ id }) => ({ assigned_to: id, joint_assigned_to: undefined }),
    JOINT: ({ taxPayerId, spouseId }) => ({
      assigned_to: taxPayerId,
      joint_assigned_to: spouseId,
    }),
    UNASSIGNED: () => ({
      assigned_to: undefined,
      joint_assigned_to: undefined,
    }),
  }) as { assigned_to?: string; joint_assigned_to?: string }
}

export function mapExtractionStatus(
  status: GetWorklayerJobExtractionStatusResponse,
): TxfWorklayerJobExtractionStatus {
  return match(status.status, {
    Done: () => TxfWorklayerJobExtractionStatus.Done,
    InProgress: () => TxfWorklayerJobExtractionStatus.InProgress,
    Failed: () => TxfWorklayerJobExtractionStatus.Failed,
  })
}
